import {
  DEFAULT_SNAP,
  canAddControlPoint,
  canAddCourse,
  canRemoveCourse,
  nextCourseName,
  canRemoveControlPoint,
  sessionCan,
  type EditorMode,
  type MoveConstraint,
  type SnapSettings,
} from '@rcs/editor';
import type { Project } from '@rcs/data-schema';
import { ToolButton } from '@rcs/ui';
import type { RenderQuality } from '@rcs/renderer';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { rideControlForKey, viewForKey } from './camera/shortcuts';
import { DEFAULT_VIEW, type ViewKind } from './camera/view';
import { AnalysisCharts } from './components/AnalysisCharts';
import { CoursePanel } from './components/CoursePanel';
import { ContextMenu, type ContextMenuTarget } from './components/ContextMenu';
import { SEGMENT_STYLES } from './course/segment-style';
import { SegmentLegend } from './components/SegmentLegend';
import { ControlPointList } from './components/ControlPointList';
import { createSampleCourse } from './course/sample';
import { withCourse } from './project/file';
import { createCatalogCourse, SAMPLE_CATALOG } from './project/sample';
import { useCourses } from './project/useCourses';
import {
  checkImportSize,
  exportFileName,
  exportProjectJson,
  importProjectText,
} from './project/transfer';
import { saveProject, type ProjectStore } from './storage/project-store';
import { pickSampleCourse } from './sample-course';
import { Hud } from './components/Hud';
import { PropertiesPanel, validatePatch } from './components/PropertiesPanel';
import { MoveSettings } from './components/MoveSettings';
import {
  FloatingPanel,
  FloatingToolbar,
  FullscreenKeys,
  FullscreenMenu,
  LayoutResetButton,
} from './components/FloatingWindows';
import { PlaybackBar } from './components/PlaybackBar';
import { WindowLayoutProvider } from './layout/movable';
import { RideControls } from './components/RideControls';
import { SafetyPanel } from './components/SafetyPanel';
import { RideSummary } from './components/RideSummary';
import { Viewport3D } from './components/Viewport3D';
import { messages } from './i18n/ja';
import {
  DEFAULT_ENVIRONMENT_ID,
  DEFAULT_VEHICLE_ID,
  nameSchema,
  findEnvironment,
  findVehicle,
} from '@rcs/data-schema';
import { rideSimulation } from './course/simulation';
import { toVehicleModelSpec } from './course/vehicle-spec';
import { toEnvironmentLook } from './course/environment-look';
import { EnvironmentPanel } from './components/EnvironmentPanel';
import { VehiclePanel } from './components/VehiclePanel';
import { PlaybackController } from './playback/controller';
import { evaluateRide } from './safety/evaluate';
import { loadQuality, QUALITY_OPTIONS, saveQuality } from './settings/quality';
import { useFullscreen } from './useFullscreen';
import { THROTTLE_RANGE } from './ride/control-timeline';
import { useRideControl } from './ride/useRideControl';
import { useRide } from './useRide';

/**
 * アプリのシェル。iPad 横向きを基本に、ツールバー・左右パネル・3D ビュー・下部パネルで構成する
 * （SRS 5.1）。各パネルの中身はバックログの各 Issue で実装する。
 */
export interface AppProps {
  /** 起動時のプロジェクト（復元したもの、またはサンプル） */
  readonly initialProject: Project;
  /** プロジェクトの保存先 */
  readonly store: ProjectStore;
  /** 前回のプロジェクトを復元できなかったときの理由 */
  readonly restoreError?: string | undefined;
}

export function App({ initialProject, store, restoreError }: AppProps) {
  const [mode, setMode] = useState<EditorMode>('edit');
  const [view, setView] = useState<ViewKind>(DEFAULT_VIEW);
  const [fps, setFps] = useState<number | undefined>();
  // 制御点を移動するときの制約と、グリッドスナップ
  const [constraint, setConstraint] = useState<MoveConstraint>('free');
  const [snap, setSnap] = useState<SnapSettings>(DEFAULT_SNAP);
  const [quality, setQuality] = useState<RenderQuality>(() => loadQuality());
  const t = messages;
  // 編集：コースと選択、取り消し・やり直しの履歴（@rcs/editor）。仮のコースから始める
  // 初回起動はサンプルプロジェクト（B-47）。画面の確認用に、URL の `?course=` で別の仮のコースを選べる
  const courses = useCourses(
    initialProject,
    (() => {
      const name = new URLSearchParams(window.location.search).get('course');
      return name === null || name === 'default' ? undefined : createSampleCourse(name);
    })(),
  );
  const { project, editor, dispatch, dirty } = courses;
  const can = sessionCan(courses.session);
  // 保存：最後に保存してから、コースの編集・追加・削除などがあれば、未保存の変更がある
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'failed'>('idle');
  const { beginSave, markSaved } = courses;
  const save = useCallback(async () => {
    // 保存の途中の操作（コースの切り替え・追加など）を打ち消さないよう、保存が終わるまで次の保存はしない
    if (saveState === 'saving') return;
    const course = editor.course;
    const version = beginSave();
    setSaveState('saving');
    try {
      const next = withCourse(project, course, new Date().toISOString());
      await saveProject(store, next);
      markSaved(course, version);
      setSaveState('idle');
    } catch {
      setSaveState('failed');
    }
  }, [editor.course, project, store, beginSave, markSaved, saveState]);
  // 走行条件：コースの車両・環境（重力・空気密度・車両の質量と抵抗）と、仮のコースの初速など
  const vehicle = findVehicle(editor.course.defaultVehicleId ?? DEFAULT_VEHICLE_ID);
  const environment = findEnvironment(
    editor.course.defaultEnvironmentId ?? DEFAULT_ENVIRONMENT_ID,
    project.environments,
  );
  // 3D の背景の見た目（背景を変えたときだけ作り直す）
  const environmentLook = useMemo(
    () => (environment ? toEnvironmentLook(environment) : undefined),
    [environment],
  );
  // 3D の車両の見た目（車両を変えたときだけ作り直す）
  const vehicleModel = useMemo(
    () => (vehicle ? toVehicleModelSpec(vehicle) : undefined),
    [vehicle],
  );
  const sampleSimulation = useMemo(() => pickSampleCourse(window.location.search).simulation, []);
  // 物理に使うのは重力と空気密度だけ。光などを変えても、走行は計算し直さない
  const gravity = environment?.physics.gravity;
  const airDensity = environment?.physics.airDensity;
  const simulation = useMemo(
    () =>
      vehicle && gravity !== undefined && airDensity !== undefined
        ? rideSimulation(
            vehicle,
            { physics: { gravity, airDensity, wind: [0, 0, 0] } },
            sampleSimulation,
          )
        : sampleSimulation,
    [vehicle, gravity, airDensity, sampleSimulation],
  );
  // 一度でも編集したら、結果が変わっても自動では再生しない
  const [edited, setEdited] = useState(false);
  const fullscreen = useFullscreen();
  // 全画面のときは、ツールバー・パネルを 3D の上に浮かぶウィンドウにする（既定では隠し、3D を広く見せる）
  const floating = fullscreen.active;

  // 数字キーで視点を切り替える（FR-CAM-011）
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const next = viewForKey(event);
      if (next !== undefined) setView(next);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);
  // 制御点の長押しメニュー
  const [menu, setMenu] = useState<ContextMenuTarget | undefined>();
  // 書き出し・読み込み（JSON ファイル）
  const fileInput = useRef<HTMLInputElement>(null);
  const [importErrors, setImportErrors] = useState<readonly string[] | undefined>();
  const exportFile = (): void => {
    const current = withCourse(project, editor.course, new Date().toISOString());
    const url = URL.createObjectURL(
      new Blob([exportProjectJson(current)], { type: 'application/json' }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = exportFileName(current);
    document.body.append(link);
    link.click();
    link.remove();
    // すぐに解放するとダウンロードが始まる前に無効になることがあるため、少し待つ
    window.setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 10_000);
  };
  const importFile = async (file: File): Promise<void> => {
    const sizeError = checkImportSize(file.size);
    if (sizeError !== undefined) {
      setImportErrors([sizeError]);
      return;
    }
    const result = importProjectText(await file.text());
    if (!result.ok) {
      setImportErrors(result.reasons);
      return;
    }
    const course = result.project.courses[0];
    if (!course) {
      setImportErrors(['コースが含まれていません']);
      return;
    }
    if (dirty && !window.confirm(t.transfer.confirmReplace)) return;
    setImportErrors(undefined);
    courses.replaceProject(result.project);
    setEdited(true);
    setSaveState('idle');
  };

  // 未保存のまま離れようとしたら警告する（NFR-REL-004）。Ctrl / Cmd + S で保存する
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = t.storage.leaveWarning;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [dirty, t.storage.leaveWarning]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.key.toLowerCase() !== 's') {
        return;
      }
      event.preventDefault();
      void save();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [save]);
  const outcome = useRide(editor.course, simulation);
  const ride = outcome?.status === 'ok' ? outcome.value : undefined;
  // 結果が変わるたびに、最初から再生するコントローラーを作る
  // 編集で計算し直したときは、動きを止めて最初の位置に置く（最初の計算だけは、自動で再生する）
  const playback = useMemo(
    () => (ride ? new PlaybackController(ride.result.meta.duration, !edited) : undefined),
    [ride, edited],
  );
  // 走行中の操作（押している間の加速・ブースト）。加速の強さは保存しない（一時的な状態）
  const [throttle, setThrottle] = useState<number>(THROTTLE_RANGE.initial);
  const control = useRideControl(ride, editor.course, simulation, playback, THROTTLE_RANGE.initial);
  // 操作を反映した走行（グラフ・評価・要約・HUD は、実際の走りで表示する）
  const liveRide = useMemo(
    () =>
      ride && control.controlled
        ? { ...ride, result: control.controlled.result, stats: control.controlled.stats }
        : ride,
    [ride, control.controlled],
  );
  const liveOutcome = useMemo(
    () => (outcome?.status === 'ok' && liveRide ? { ...outcome, value: liveRide } : outcome),
    [outcome, liveRide],
  );
  // 走行の評価（失速・G の超過）。車両の限界値は、既定の車両のもの
  const warnings = useMemo(() => {
    return liveRide && vehicle ? evaluateRide(liveRide.result, vehicle.limits) : undefined;
  }, [liveRide, vehicle]);
  // 走行モードでは、↑ キーを押している間加速し、B キーでブーストする（FR-SIM-008）
  const riding = mode === 'ride';
  const { press, release, boost } = control;
  useEffect(() => {
    if (!riding) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      const key = rideControlForKey(event);
      if (key === undefined) return;
      event.preventDefault();
      if (event.repeat) return;
      if (key === 'throttle') press();
      else boost();
    };
    const onKeyUp = (event: KeyboardEvent): void => {
      if (event.key === 'ArrowUp') release();
    };
    const onBlur = (): void => {
      release();
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      release();
    };
  }, [riding, press, release, boost]);
  // 3D ビューに出す制御点の目印。選択中のものは強調する
  const markers = useMemo(
    () =>
      editor.course.controlPoints.map((point) => ({
        id: point.id,
        position: point.position,
        selected: point.id === editor.selectedId,
        color: SEGMENT_STYLES[point.segment]?.color,
      })),
    [editor.course.controlPoints, editor.selectedId],
  );
  // 制御点を編集できるのは、編集モードで、全体俯瞰のとき
  const editing = mode === 'edit' && view === 'overview';

  // Delete / Backspace：選択中の制御点を削除する（編集中のみ）
  const selectedId = editor.selectedId;
  useEffect(() => {
    if (!editing) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      const target = event.target as { tagName?: string; isContentEditable?: boolean } | null;
      if (
        target?.isContentEditable === true ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName ?? '')
      ) {
        return;
      }
      if (selectedId === undefined) return;
      event.preventDefault();
      setEdited(true);
      dispatch({ type: 'remove', id: selectedId });
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [editing, selectedId, dispatch]);

  // 取り消し・やり直しのキー（Ctrl / Cmd + Z、Shift を加えるか Ctrl + Y でやり直し）
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
      const target = event.target as { tagName?: string; isContentEditable?: boolean } | null;
      if (
        target?.isContentEditable === true ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName ?? '')
      )
        return;
      const key = event.key.toLowerCase();
      if (key === 'z' && !event.shiftKey) dispatch({ type: 'undo' });
      else if ((key === 'z' && event.shiftKey) || key === 'y') dispatch({ type: 'redo' });
      else return;
      event.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [dispatch]);

  return (
    <WindowLayoutProvider kind={floating ? 'fullscreen' : 'normal'}>
      <div className={floating ? 'app-shell app-shell--fullscreen' : 'app-shell'}>
        <FloatingToolbar floating={floating}>
          <h1 className="toolbar__title">{t.appName}</h1>
          <span className="toolbar__project" data-testid="project-name">
            {project.name}
          </span>
          <span
            className="toolbar__save-status"
            data-testid="save-status"
            data-dirty={dirty}
            role="status"
          >
            {saveState === 'failed'
              ? `✕ ${t.storage.saveFailed}`
              : saveState === 'saving'
                ? t.storage.saving
                : dirty
                  ? `● ${t.storage.unsaved}`
                  : `✓ ${t.storage.saved}`}
          </span>
          <div className="toolbar__spacer" />
          <div className="toolbar__group">
            <ToolButton
              label={t.toolbar.save}
              disabled={!dirty || saveState === 'saving'}
              onClick={() => {
                void save();
              }}
              data-testid="save"
            />
            <ToolButton label={t.toolbar.export} onClick={exportFile} data-testid="export" />
            <ToolButton
              label={t.toolbar.import}
              onClick={() => {
                fileInput.current?.click();
              }}
              data-testid="import"
            />
            <input
              ref={fileInput}
              type="file"
              accept=".json,application/json"
              hidden
              data-testid="import-input"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) void importFile(file);
              }}
            />
            <ToolButton
              label={t.toolbar.undo}
              disabled={!can.undo}
              onClick={() => {
                dispatch({ type: 'undo' });
              }}
              data-testid="undo"
            />
            <ToolButton
              label={t.toolbar.redo}
              disabled={!can.redo}
              onClick={() => {
                dispatch({ type: 'redo' });
              }}
              data-testid="redo"
            />
          </div>
          <div className="toolbar__group" role="group" aria-label={t.toolbar.modeGroup}>
            <ToolButton
              label={t.toolbar.modeEdit}
              pressed={mode === 'edit'}
              onClick={() => {
                setMode('edit');
                // 制御点を編集できるのは全体俯瞰のときだけなので、編集に戻ると全体俯瞰へ戻す
                setView('overview');
              }}
            />
            <ToolButton
              label={t.toolbar.modeRide}
              pressed={mode === 'ride'}
              onClick={() => {
                setMode('ride');
              }}
            />
          </div>
          <label className="toolbar__select">
            <span>{t.settings.quality}</span>
            <select
              value={quality}
              data-testid="quality-select"
              onChange={(event) => {
                const next = event.target.value as RenderQuality;
                setQuality(next);
                saveQuality(next);
              }}
            >
              {QUALITY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {t.settings.qualityOptions[option]}
                </option>
              ))}
            </select>
          </label>
          <ToolButton
            label={fullscreen.active ? t.toolbar.exitFullscreen : t.toolbar.fullscreen}
            pressed={fullscreen.active}
            onClick={fullscreen.toggle}
            data-testid="fullscreen-toggle"
          />
          <ToolButton
            label={t.toolbar.testRide}
            onClick={() => {
              // ワンタップで試乗：乗客視点にして、最初から再生する
              setMode('ride');
              setView('passenger');
              playback?.stop();
              playback?.play();
            }}
            disabled={playback === undefined}
            data-testid="test-ride"
          />
          {!floating && <LayoutResetButton />}
        </FloatingToolbar>

        {menu !== undefined && (
          <ContextMenu
            target={menu}
            canAdd={canAddControlPoint(editor)}
            canRemove={canRemoveControlPoint(editor, menu.id)}
            onAddAfter={() => {
              setEdited(true);
              dispatch({ type: 'add', afterId: menu.id });
            }}
            onRemove={() => {
              setEdited(true);
              dispatch({ type: 'remove', id: menu.id });
            }}
            onClose={() => {
              setMenu(undefined);
            }}
          />
        )}

        {importErrors !== undefined && (
          <div className="import-error" role="alert" data-testid="import-error">
            <strong>⚠ {t.transfer.importFailedTitle}</strong>
            <p>{t.transfer.importFailedBody}</p>
            <ul>
              {importErrors.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
            <ToolButton
              label={t.transfer.dismiss}
              onClick={() => {
                setImportErrors(undefined);
              }}
              data-testid="import-error-dismiss"
            />
          </div>
        )}

        {restoreError !== undefined && (
          <p className="restore-error" role="alert" data-testid="restore-error">
            ⚠ {t.storage.restoreFailed}
            {restoreError}
          </p>
        )}

        {floating && (
          <>
            <FullscreenMenu onExit={fullscreen.toggle} />
            <FullscreenKeys />
          </>
        )}

        <FloatingPanel
          id="left"
          title={t.panels.objects}
          className="app-shell__left"
          floating={floating}
        >
          <VehiclePanel
            vehicle={vehicle}
            onSelect={(id) => {
              setEdited(true);
              dispatch({ type: 'set-vehicle', id });
            }}
          />
          <EnvironmentPanel
            environment={environment}
            onSelect={(id) => {
              setEdited(true);
              dispatch({ type: 'set-environment', id });
            }}
          />
          <CoursePanel
            courses={project.courses}
            currentId={editor.course.id}
            canAdd={canAddCourse(project)}
            canRemove={canRemoveCourse(project, editor.course.id)}
            onSelect={(id) => {
              setMenu(undefined);
              courses.select(id);
            }}
            onAdd={() => {
              const now = new Date().toISOString();
              courses.add(
                createSampleCourse('default', {
                  id: crypto.randomUUID(),
                  name: nextCourseName(project),
                  now,
                }),
              );
            }}
            onDuplicate={() => {
              courses.duplicate(crypto.randomUUID(), new Date().toISOString());
            }}
            samples={SAMPLE_CATALOG.map((entry) => ({ key: entry.sample, name: entry.name }))}
            onAddSample={(key) => {
              const entry = SAMPLE_CATALOG.find((item) => item.sample === key);
              if (!entry) return;
              setMenu(undefined);
              courses.add(
                createCatalogCourse(entry, new Date().toISOString(), crypto.randomUUID()),
              );
            }}
            onRemove={(id) => {
              setMenu(undefined);
              courses.remove(id);
            }}
            onRename={(name) => {
              const valid = nameSchema.safeParse(name).success;
              if (valid) dispatch({ type: 'rename', name });
              return valid;
            }}
          />
          <p className="placeholder">{t.controlPoints.hint}</p>
          <MoveSettings
            constraint={constraint}
            snap={snap}
            onConstraintChange={setConstraint}
            onSnapChange={setSnap}
          />
          <ControlPointList
            state={editor}
            onSelect={(id) => {
              dispatch({ type: 'select', id });
            }}
            onAdd={() => {
              setEdited(true);
              dispatch({ type: 'add' });
            }}
            onRemove={(id) => {
              setEdited(true);
              dispatch({ type: 'remove', id });
            }}
          />
        </FloatingPanel>

        <main className="app-shell__viewport">
          <Viewport3D
            ride={ride}
            result={liveRide?.result}
            fitKey={outcome?.courseId}
            sections={outcome?.sections}
            segmentKinds={outcome?.segmentKinds}
            vehicleModel={vehicleModel}
            environmentLook={environmentLook}
            playback={playback}
            quality={quality}
            view={view}
            onViewChange={setView}
            onFps={setFps}
            editing={editing}
            dragPlane={constraint}
            controlPoints={markers}
            onControlPointSelect={(id) => {
              dispatch({ type: 'select', id });
            }}
            onControlPointMoveStart={(id) => {
              setEdited(true);
              dispatch({ type: 'move-start', id });
            }}
            onControlPointMove={(id, position) => {
              dispatch({ type: 'move', id, position, constraint, snap });
            }}
            onControlPointMoveEnd={(id) => {
              dispatch({ type: 'move-end', id });
            }}
            onControlPointLongPress={(id, x, y) => {
              setMenu({ id, x, y });
            }}
          />
          <SegmentLegend kinds={editor.course.controlPoints.map((p) => p.segment)} />
          <Hud
            ride={liveRide}
            segmentKinds={outcome?.segmentKinds}
            controller={playback}
            fps={fps}
          />
          <PlaybackBar controller={playback}>
            {riding && liveRide && playback && (
              <RideControls
                track={liveRide.track}
                result={liveRide.result}
                segmentKinds={outcome?.segmentKinds}
                controller={playback}
                control={control}
                acceleration={throttle}
                onAccelerationChange={(value) => {
                  setThrottle(value);
                  control.setAcceleration(value);
                }}
              />
            )}
          </PlaybackBar>
        </main>

        <FloatingPanel
          id="right"
          title={t.panels.properties}
          className="app-shell__right"
          floating={floating}
        >
          <PropertiesPanel
            state={editor}
            onUpdate={(id, patch) => {
              const error = validatePatch(editor, id, patch);
              if (error === undefined) {
                setEdited(true);
                dispatch({ type: 'update', id, patch });
              }
              return error;
            }}
            onSetClosed={(closed) => {
              setEdited(true);
              dispatch({ type: 'set-closed', closed });
            }}
          />
        </FloatingPanel>

        <FloatingPanel
          id="bottom"
          title={t.panels.analysis}
          className="app-shell__bottom"
          floating={floating}
        >
          <AnalysisCharts ride={liveRide} controller={playback} warnings={warnings} />
          <SafetyPanel warnings={warnings} />
          <RideSummary outcome={liveOutcome} />
          <p className="disclaimer" data-testid="disclaimer">
            {t.disclaimer}
          </p>
        </FloatingPanel>
      </div>
    </WindowLayoutProvider>
  );
}
